import React, { useRef, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animated components with delay props (will be defined after base components)
let AnimatedSectionContainer: any;
let AnimatedLogoContainer: any;
let AnimatedTextContainer: any;

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 0;
  padding-top: 0;
`;

const LogoContainer = styled.div`
  margin: 18px;
  padding: 18px;
  user-select: none;
`;

const TextContainer = styled.div`
  max-width: 800px;
  width: 100%;
  padding: 0 20px;
  margin-top: 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 30px; // Increased padding for mobile
    width: 100%; // Ensure full width on mobile
    max-width: 100%; // Allow container to take full width on mobile
  }
`;

const SmallText = styled.p`
  font-size: 1rem;
  line-height: 1.4;
  color: var(--color-text);
  text-align: left;
  margin-bottom: 10px;
  font-family: var(--font-secondary);
`;

// Define animated components after base components
AnimatedSectionContainer = styled(SectionContainer)<{ isVisible: boolean }>`
  animation: ${({ isVisible }) => isVisible ? fadeInUp : 'none'} 0.8s ease-out forwards;
  opacity: 0;
`;

AnimatedLogoContainer = styled(LogoContainer)<{ delay?: number; isVisible: boolean }>`
  animation: ${({ isVisible }) => isVisible ? fadeInUp : 'none'} 0.8s ease-out forwards;
  animation-delay: ${({ delay }) => delay || 0}s;
  opacity: 0;
`;

AnimatedTextContainer = styled(TextContainer)<{ delay?: number; isVisible: boolean }>`
  animation: ${({ isVisible }) => isVisible ? fadeInUp : 'none'} 0.8s ease-out forwards;
  animation-delay: ${({ delay }) => delay || 0}s;
  opacity: 0;
`;

const NinjaTraderSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated]);

  return (
    <AnimatedSectionContainer id="ninjatrader-logo" isVisible={isVisible} ref={sectionRef}>
      <AnimatedLogoContainer isVisible={isVisible} delay={0.2}>
        <a href="https://ninjatrader.com" target="_blank" rel="noopener noreferrer">
          <img 
            src="/assets/NinjaTrader_Wordmark_color_RGB.png" 
            alt="NinjaTrader Logo" 
            style={{ height: '30px', width: 'auto' }} 
          />
        </a>
      </AnimatedLogoContainer>
      <AnimatedTextContainer isVisible={isVisible} delay={0.4}>
        <SmallText>
          <strong>Risk Disclosure:</strong> Futures and forex trading contains substantial risk and is not for every investor. An investor could potentially lose all or more than the initial investment. Risk capital is money that can be lost without jeopardizing one's financial security or lifestyle. Only risk capital should be used for trading and only those with sufficient risk capital should consider trading. Past performance is not necessarily indicative of future results.
        </SmallText>
        <SmallText>
          <strong>NinjaTrader Disclaimer:</strong> NinjaTrader® is a registered trademark of NinjaTrader Group, LLC. No NinjaTrader company has any affiliation with the owner, developer, or provider of the products or services described herein, or any interest, ownership, or otherwise, in any such product or service, or endorses, recommends, or approves any such product or service.
        </SmallText>
      </AnimatedTextContainer>
    </AnimatedSectionContainer>
  );
};

export default NinjaTraderSection;
